import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenantListApi } from "@/lib/api/services/tenant-list.api";
import type { TenantListKind, TenantLists } from "@/types/tenant-list";

export const tenantListKeys = {
  all: ["tenant-lists"] as const,
};

/**
 * The tenant's configuration lists. Read by anyone who can read a recipe,
 * because every one of them backs a picker somewhere in the recipe form.
 */
export function useTenantLists(enabled = true) {
  return useQuery({
    queryKey: tenantListKeys.all,
    queryFn: () => tenantListApi.get(),
    enabled,
    // Lists change when an administrator opens Configuration, which is rare;
    // a picker refetching them on every mount is noise.
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * One list, or `undefined` while it loads.
 *
 * Undefined rather than an empty array: a caller has its own seed to fall back
 * on, and an empty list is a thing a tenant is allowed to have meant.
 */
export function useTenantList(kind: TenantListKind, enabled = true): string[] | undefined {
  return useTenantLists(enabled).data?.lists[kind];
}

export function useUpdateTenantList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, values }: { kind: TenantListKind; values: string[] }) =>
      tenantListApi.update(kind, values),
    onSuccess: (data: TenantLists) => {
      queryClient.setQueryData(tenantListKeys.all, data);
    },
  });
}
